import { PERPETUAL_DEX_ADDRESSES } from "constants/perpetual-dex/addresses";
import { BigNumber, ethers } from "ethers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IL1StandardBridge, L1DepositHelper } from "../../codegen";
import { CollateralAssetResponse } from "../../codegen-api";
import {
  DepositWithdrawCollaterals,
  getCollateralERC20Addresses,
  getSocketConnectorAddress,
} from "../../constants/addresses";
import {
  SOCKET_DEPOSIT_CHAINS,
  UNIFIED_VAULT_CHAIN_IDS,
} from "../../enums/chain";
import { ISignature } from "../../interfaces/Signing";
import useSocketApi from "../api/socketApi/useSocketApi";
import { IWallet } from "../wallet/useWallet";
import { validateEstimatedGas } from "./shared";
import {
  getGasLimit,
  getL1BridgeContract,
  getL1DepositHelperContract,
  l1GasLimit,
  l2GasLimit,
} from "./useDeposit.helpers";
import { IDepositERC20MultichainResponse } from "./useDeposit.types";
import useSocketHelper from "./useSocketHelper";
import useSocketVault from "./useSocketVault";

const useDeposit = (
  wallet: IWallet,
  collateral?: DepositWithdrawCollaterals
) => {
  const { account, provider, chainId } = wallet;
  const { depositFees, withdrawalFees } = useSocketApi(chainId, collateral);
  const { socketHelper } = useSocketHelper({ provider, chainId });
  const { vault } = useSocketVault({ provider, chainId, collateral });

  const [l1DepositHelper, setL1DepositHelper] = useState<L1DepositHelper>();
  const [l1StandardBridge, setL1StandardBridge] = useState<IL1StandardBridge>();

  // Returns the correct spender to approve to based on the chainId
  const approvalSpender = useMemo(() => {
    if (!chainId) {
      return undefined;
    }

    if (UNIFIED_VAULT_CHAIN_IDS.includes(chainId)) {
      return PERPETUAL_DEX_ADDRESSES[chainId]?.unifiedVault;
    } else if (SOCKET_DEPOSIT_CHAINS.includes(chainId)) {
      return socketHelper?.address;
    } else {
      return l1StandardBridge?.address;
    }
  }, [chainId, l1StandardBridge?.address, socketHelper?.address]);

  useEffect(() => {
    setL1DepositHelper(getL1DepositHelperContract(provider, chainId));
    setL1StandardBridge(getL1BridgeContract(provider, chainId));
  }, [chainId, provider]);

  const connectorAddress = useMemo(
    () =>
      collateral
        ? getSocketConnectorAddress(collateral, "deposit", chainId)
        : undefined,
    [chainId, collateral]
  );

  const depositERC20WithPermit = useCallback(
    (
      l1TokenAddress: string,
      l2TokenAddress: string,
      amount: BigNumber,
      deadline: number,
      signature: ISignature,
      isYieldVault: boolean = false
    ) => {
      if (!l1DepositHelper) {
        return undefined;
      }

      // Encode the data in the expected structure
      const encodedData = ethers.utils.defaultAbiCoder.encode(
        ["tuple(bool depositToYieldVault)"],
        [{ depositToYieldVault: true }]
      );
      const dataAsHex = isYieldVault ? encodedData : [];

      return l1DepositHelper.depositERC20WithPermit(
        l1TokenAddress,
        l2TokenAddress,
        amount,
        l2GasLimit,
        dataAsHex,
        deadline,
        signature.v,
        signature.r,
        signature.s,
        {
          gasLimit: getGasLimit(chainId),
        }
      );
    },
    [chainId, l1DepositHelper]
  );

  const depositETH = useCallback(
    (amount: BigNumber) => {
      const { l2 } = getCollateralERC20Addresses(
        CollateralAssetResponse.Weth,
        chainId
      );

      if (!l1DepositHelper || !l2) {
        return undefined;
      }

      return l1DepositHelper.depositWETH(l2, l2GasLimit, [], {
        value: amount,
        gasLimit: getGasLimit(chainId),
      });
    },
    [chainId, l1DepositHelper]
  );

  const depositERC20 = useCallback(
    async (
      l1TokenAddress: string,
      l2TokenAddress: string,
      amount: BigNumber,
      isYieldVault: boolean = false
    ) => {
      if (!l1StandardBridge) {
        return undefined;
      }

      // Encode the data as per the expected structure
      const encodedData = ethers.utils.defaultAbiCoder.encode(
        ["tuple(bool depositToYieldVault)"],
        [{ depositToYieldVault: true }]
      );

      const dataAsHex = isYieldVault ? encodedData : [];

      const tx = await l1StandardBridge.depositERC20(
        l1TokenAddress,
        l2TokenAddress,
        amount,
        l2GasLimit,
        dataAsHex,
        {
          gasLimit: getGasLimit(chainId),
        }
      );
      return tx;
    },
    [chainId, l1StandardBridge]
  );

  // Deposit from optimism or arbitrum
  const depositERC20OptimismOrArbitrum = useCallback(
    async (
      amount: BigNumber,
      tokenAddress: string,
      assetToDeposit: DepositWithdrawCollaterals,
      isYieldVault: boolean = false
    ): Promise<IDepositERC20MultichainResponse | undefined> => {
      if (!vault || !account || !connectorAddress || !socketHelper) {
        return undefined;
      }

      if (!depositFees) {
        throw Error("No deposit fees");
      }

      // Encode the data as per the expected structure
      const encodedData = ethers.utils.defaultAbiCoder.encode(
        ["tuple(bool depositToYieldVault)"],
        [{ depositToYieldVault: true }]
      );

      const dataAsHex = isYieldVault ? encodedData : [];

      // Check limit
      const limit = await vault.getCurrentLockLimit(connectorAddress);

      // OK
      if (amount.lte(limit)) {
        const estimatedGas = await validateEstimatedGas(
          socketHelper.estimateGas.depositToAppChain(
            account,
            tokenAddress,
            amount,
            l1GasLimit,
            connectorAddress,
            dataAsHex,
            {
              value: depositFees,
            }
          ),
          BigNumber.from("800000")
        );
        const tx = await socketHelper.depositToAppChain(
          account,
          tokenAddress,
          amount,
          l1GasLimit,
          connectorAddress,
          dataAsHex,
          {
            value: depositFees,
            gasLimit: estimatedGas || 800000,
          }
        );
        return {
          insufficientLimit: undefined,
          tx,
        };
      }
      // NOT ENOUGH LIMIT
      return {
        insufficientLimit: limit,
        tx: undefined,
      };
    },
    [account, connectorAddress, depositFees, socketHelper, vault]
  );

  const depositETHOptimismOrArbitrum = useCallback(
    async (
      amount: BigNumber
    ): Promise<IDepositERC20MultichainResponse | undefined> => {
      if (!vault || !account || !connectorAddress || !socketHelper) {
        return undefined;
      }

      if (!depositFees) {
        throw Error("No deposit fees");
      }

      // Check limit
      const limit = await vault.getCurrentLockLimit(connectorAddress);

      // OK
      if (amount.lte(limit)) {
        const estimatedGas = await validateEstimatedGas(
          socketHelper.estimateGas.depositETHToAppChain(
            account,
            amount,
            l1GasLimit,
            connectorAddress,
            [],
            {
              value: depositFees.add(amount),
            }
          ),
          BigNumber.from("800000")
        );
        const tx = await socketHelper.depositETHToAppChain(
          account,
          amount,
          l1GasLimit,
          connectorAddress,
          [],
          {
            value: depositFees.add(amount),
            gasLimit: estimatedGas || 800000,
          }
        );
        return {
          insufficientLimit: undefined,
          tx,
        };
      }
      // NOT ENOUGH LIMIT
      return {
        insufficientLimit: limit,
        tx: undefined,
      };
    },
    [account, connectorAddress, depositFees, socketHelper, vault]
  );

  return {
    l1DepositHelper,
    socketHelper,
    depositETH,
    depositERC20,
    depositERC20WithPermit,
    depositERC20OptimismOrArbitrum,
    depositETHOptimismOrArbitrum,
    approvalSpender,
    depositFees,
    withdrawalFees,
  };
};

export default useDeposit;
